import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import styles from "./mantenedoraUsers.module.css";
import Button from "../../../components/button/button";
import Badge from "../../../components/badge/badge";
import AddCircle from "../../../assets/icons/add-circle.svg";
import EditIcon from "../../../assets/icons/edit.svg";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import { useMantenedora } from "../../../context/MantenedoraContext";
import {
  readMantenedoraUsers,
  updatePrimaryInstitution,
} from "../../../services/mantenedora";
import CreateUserModal from "./createUserModal/createUserModal";
import InviteUserModal from "./inviteUserModal/inviteUserModal";

const MantenedoraUsers = () => {
  const { mantenedora, institutions: mantenedoraInstitutions } = useMantenedora();
  const [loading, setLoading] = useState(true);
  const [usersData, setUsersData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'mantenedora' ou 'institution'
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteModalType, setInviteModalType] = useState(null);
  const [selectedInstitutionForInvite, setSelectedInstitutionForInvite] = useState(null);
  const [savingPrimaryUserId, setSavingPrimaryUserId] = useState(null);

  useEffect(() => {
    if (mantenedora?.id) {
      fetchUsers();
    }
  }, [mantenedora?.id]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await readMantenedoraUsers(mantenedora.id);
      setUsersData(data);
    } catch (error) {
      toast.error("Erro ao carregar usuários");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMantenedoraUser = () => {
    setModalType("mantenedora");
    setSelectedInstitution(null);
    setShowModal(true);
  };

  const handleAddInstitutionUser = (institution) => {
    setModalType("institution");
    setSelectedInstitution(institution);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setModalType(null);
    setSelectedInstitution(null);
  };

  const handleInviteMantenedoraUser = () => {
    setInviteModalType("mantenedora");
    setSelectedInstitutionForInvite(null);
    setShowInviteModal(true);
  };

  const handleInviteInstitutionUser = (institution) => {
    setInviteModalType("institution");
    setSelectedInstitutionForInvite(institution);
    setShowInviteModal(true);
  };

  const handleInviteModalClose = () => {
    setShowInviteModal(false);
    setInviteModalType(null);
    setSelectedInstitutionForInvite(null);
  };

  const handleInviteSent = () => {
    handleInviteModalClose();
    toast.success("Convite enviado por e-mail com sucesso!");
  };

  const handleUserCreated = () => {
    handleModalClose();
    fetchUsers();
    toast.success("Usuário criado com sucesso!");
  };

  const handlePrimaryInstitutionChange = async (userId, institutionId) => {
    const newValue = institutionId || null;
    try {
      setSavingPrimaryUserId(userId);
      await updatePrimaryInstitution(mantenedora.id, userId, newValue);
      setUsersData((prev) =>
        prev
          ? {
              ...prev,
              mantenedoraUsers: prev.mantenedoraUsers.map((u) =>
                u.id === userId ? { ...u, primaryInstitutionId: newValue } : u
              ),
            }
          : prev
      );
      toast.success("Instituição primária atualizada");
    } catch (error) {
      toast.error(error.message || "Erro ao definir instituição primária");
    } finally {
      setSavingPrimaryUserId(null);
    }
  };

  const buildEditState = (user, scope, institutionId = null) => ({
    user: institutionId ? { ...user, institutionId } : user,
    scope,
    institutions: usersData?.institutionUsers || [],
  });

  const translateRole = (role) => {
    const roleMap = {
      MantenedoraAdmin: "Admin da Mantenedora",
      MantenedoraManager: "Gerente da Mantenedora",
      Admin: "Administrador",
      Manager: "Gerente",
      Operator: "Operador",
      Financial: "Financeiro",
      Academic: "Acadêmico",
    };
    return roleMap[role] || role || "-";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  if (loading) {
    return (
      <div>
        <HeaderTitle title="Gestão de Usuários" />
        <div className="mainContentSpace">
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderTitle title="Gestão de Usuários">
        <div className={styles.headerInfo}>
          <span className={styles.totalUsers}>
            Total: {usersData?.totalUsers || 0} usuários
          </span>
        </div>
      </HeaderTitle>

      <div className="mainContentSpace">
        {/* Seção: Usuários da Mantenedora */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <h3>Usuários da Mantenedora</h3>
              <Badge
                text={`${usersData?.totalMantenedoraUsers || 0} usuários`}
                color="blue"
              />
            </div>
            <div className={styles.headerActions}>
              <Button
                icon={AddCircle}
                text="Convidar por e-mail"
                size="normal"
                variation="primaryInverse"
                onClick={handleInviteMantenedoraUser}
              />
              <Button
                icon={AddCircle}
                text="Adicionar Usuário"
                size="normal"
                variation="primary"
                onClick={handleAddMantenedoraUser}
              />
            </div>
          </div>

          {usersData?.mantenedoraUsers?.length > 0 ? (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Perfil</th>
                    <th>Telefone</th>
                    <th>Criado em</th>
                    <th>Instituição Primária</th>
                    <th className={styles.actionsHeader}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usersData.mantenedoraUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{`${user.firstName} ${user.lastName}`}</td>
                      <td className={styles.primary}>{user.email}</td>
                      <td>
                        <Badge
                          text={translateRole(user.role)}
                          color={
                            user.role === "MantenedoraAdmin" ? "purple" : "blue"
                          }
                        />
                      </td>
                      <td>{user.phoneNumber || "-"}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        <select
                          className={styles.primarySelect}
                          value={user.primaryInstitutionId || ""}
                          disabled={
                            savingPrimaryUserId === user.id ||
                            !(mantenedoraInstitutions?.length > 0)
                          }
                          onChange={(e) =>
                            handlePrimaryInstitutionChange(
                              user.id,
                              e.target.value
                            )
                          }
                          title={
                            mantenedoraInstitutions?.length > 0
                              ? "Instituição que o usuário acessa automaticamente ao logar"
                              : "Nenhuma instituição cadastrada na mantenedora"
                          }
                        >
                          <option value="">Nenhuma</option>
                          {(mantenedoraInstitutions || []).map((inst) => (
                            <option key={inst.id} value={inst.id}>
                              {inst.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={styles.actionsCell}>
                        <Link
                          to={`/app/gestao-usuarios/edit/${user.id}`}
                          state={buildEditState(user, "mantenedora")}
                        >
                          <Button
                            icon={EditIcon}
                            text="Editar"
                            size="normal"
                            variation="primaryInverse"
                          />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.emptyMessage}>
              Nenhum usuário da mantenedora cadastrado
            </p>
          )}
        </div>

        {/* Seção: Usuários por Instituição */}
        {usersData?.institutionUsers?.map((group) => (
          <div key={group.institutionId} className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <h3>{group.institutionName}</h3>
                {group.city && group.state && (
                  <span className={styles.institutionLocation}>
                    {group.city}, {group.state}
                  </span>
                )}
                <Badge text={`${group.users.length} usuários`} color="green" />
              </div>
              <div className={styles.headerActions}>
                <Button
                  icon={AddCircle}
                  text="Convidar por e-mail"
                  size="normal"
                  variation="primaryInverse"
                  onClick={() =>
                    handleInviteInstitutionUser({
                      id: group.institutionId,
                      name: group.institutionName,
                    })
                  }
                />
                <Button
                  icon={AddCircle}
                  text="Adicionar Usuário"
                  size="normal"
                  variation="primary"
                  onClick={() =>
                    handleAddInstitutionUser({
                      id: group.institutionId,
                      name: group.institutionName,
                    })
                  }
                />
              </div>
            </div>

            {group.users.length > 0 ? (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>E-mail</th>
                      <th>Perfil</th>
                      <th>Telefone</th>
                      <th>Criado em</th>
                      <th className={styles.actionsHeader}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.users.map((user) => (
                      <tr key={user.id}>
                        <td>{`${user.firstName} ${user.lastName}`}</td>
                        <td className={styles.primary}>{user.email}</td>
                        <td>
                          <Badge
                            text={translateRole(user.role)}
                            color={user.role === "Admin" ? "purple" : "blue"}
                          />
                        </td>
                        <td>{user.phoneNumber || "-"}</td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td className={styles.actionsCell}>
                          <Link
                            to={`/app/gestao-usuarios/edit/${user.id}`}
                            state={buildEditState(
                              user,
                              "institution",
                              group.institutionId
                            )}
                          >
                            <Button
                              icon={EditIcon}
                              text="Editar"
                              size="normal"
                              variation="primaryInverse"
                            />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.emptyMessage}>
                Nenhum usuário cadastrado nesta instituição
              </p>
            )}
          </div>
        ))}

        {usersData?.institutionUsers?.length === 0 && (
          <div className={styles.section}>
            <p className={styles.emptyMessage}>
              Nenhuma instituição cadastrada ainda
            </p>
          </div>
        )}
      </div>

      {/* Modal de Criação de Usuário */}
      {showModal && (
        <CreateUserModal
          type={modalType}
          institution={selectedInstitution}
          institutions={usersData?.institutionUsers || []}
          mantenedoraId={mantenedora?.id}
          onClose={handleModalClose}
          onSuccess={handleUserCreated}
        />
      )}

      {/* Modal de Convite por e-mail */}
      {showInviteModal && (
        <InviteUserModal
          type={inviteModalType}
          institution={selectedInstitutionForInvite}
          institutions={usersData?.institutionUsers || []}
          mantenedoraId={mantenedora?.id}
          onClose={handleInviteModalClose}
          onSuccess={handleInviteSent}
        />
      )}
    </div>
  );
};

export default MantenedoraUsers;
